

// TableManagement.jsx
import React, { useState, useEffect } from 'react';
import './Usertable.css';
import { useNavigate } from "react-router-dom";



const Usertable = () => {
  const [tables, setTables] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [bookingForm, setBookingForm] = useState({
    startTime: '',
    durationMinutes: 45,
    phoneNumber: '',
    guestName: '',
    bookingDate: '',
    capacity: '',
    tableId: '',
    paymentEnable: 'false'
  });
  const [submitting, setSubmitting] = useState(false);
const navigate = useNavigate();
  // Get auth token from localStorage or your auth context
  const getAuthToken = () => {
    // Method 1: Get from localStorage
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    
    // Method 2: Get from sessionStorage
    // const token = sessionStorage.getItem('authToken');
    
    // Method 3: If you're using a context or state management
    // const { token } = useAuth(); // Uncomment if using auth context
    
    return token;
  };

  // Common headers with authentication
  const getHeaders = () => {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      // Alternative header formats (uncomment if needed):
      // headers['x-auth-token'] = token;
      // headers['token'] = token;
    }
    
    return headers;
  };

  // Fetch all tables
  const fetchTables = async () => {
    try {
      const response = await fetch('https://backend.maamaas.com/food/api/seating/all/vendor/1', {
        method: 'GET',
        headers: getHeaders(),
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized. Please login again.');
        }
        throw new Error('Failed to fetch tables');
      }
      const data = await response.json();
      setTables(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching tables:', err);
    }
  };

  // Fetch bookings for selected date
  const fetchBookings = async () => {
    try {
      const response = await fetch(`https://backend.maamaas.com/food/api/seatingdetails/vendor/1/vendor-bookings?date=${selectedDate}`, {
        method: 'GET',
        headers: getHeaders(),
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized. Please login again.');
        }
        throw new Error('Failed to fetch bookings');
      }
      const data = await response.json();
      setBookings(data);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(err.message);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      await fetchTables();
      await fetchBookings();
      setLoading(false);
    };
    loadData();
  }, [selectedDate]);

  // Update table status based on bookings
  const getTableStatus = (table) => {
    // Check if table has any active booking for selected date
    const currentTime = new Date();
    const currentTimeStr = currentTime.toTimeString().slice(0, 8);
    
    const tableBookings = bookings.filter(booking => booking.seatingId === table.id);
    
    if (tableBookings.length > 0) {
      // Check for active/ongoing bookings
      const activeBooking = tableBookings.find(booking => {
        const bookingStart = booking.startTime;
        const bookingEnd = addMinutesToTime(bookingStart, booking.durationMinutes);
        return currentTimeStr >= bookingStart && currentTimeStr <= bookingEnd;
      });
      
      if (activeBooking) return 'occupied';
      
      // Check for future bookings today
      const futureBooking = tableBookings.find(booking => booking.startTime > currentTimeStr);
      if (futureBooking) return 'reserved';
    }
    
    return table.seatingStatus?.toLowerCase() || 'available';
  };

  const addMinutesToTime = (time, minutes) => {
    const [hours, mins, secs] = time.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(mins) + minutes, parseInt(secs || 0));
    return date.toTimeString().slice(0, 8);
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'available': return 'Available';
      case 'reserved': return 'Reserved';
      case 'occupied': return 'Occupied';
      case 'vacant': return 'Vacant';
      case 'cleaning': return 'Cleaning';
      case 'maintenance': return 'Maintenance';
      default: return status;
    }
  };

  const getStatusClass = (status) => {
    switch(status) {
      case 'available': return 'available';
      case 'reserved': return 'reserved';
      case 'occupied': return 'occupied';
      case 'vacant': return 'vacant';
      case 'cleaning': return 'cleaning';
      case 'maintenance': return 'maintenance';
      default: return 'available';
    }
  };

  // Calculate statistics
  const getStatistics = () => {
    const total = tables.length;
    const available = tables.filter(t => getTableStatus(t) === 'available').length;
    const reserved = tables.filter(t => getTableStatus(t) === 'reserved').length;
    const occupied = tables.filter(t => getTableStatus(t) === 'occupied').length;
    const vacant = tables.filter(t => t.seatingStatus?.toLowerCase() === 'vacant').length;
    const cleaning = tables.filter(t => t.seatingStatus?.toLowerCase() === 'cleaning').length;
    const maintenance = tables.filter(t => t.seatingStatus?.toLowerCase() === 'maintenance').length;
    return { total, available, reserved, occupied, vacant, cleaning, maintenance };
  };

  const stats = getStatistics();

  // Group tables by floor and capacity
  const getGroupedTables = () => {
    const floorGroups = {};
    const floorOrder = ['Ground Floor', 'Party Hall', 'First Floor', 'Second Floor'];
    
    tables.forEach(table => {
      let floorName = table.name;
      if (!floorGroups[floorName]) {
        floorGroups[floorName] = {};
      }
      const capacity = table.capacity;
      if (!floorGroups[floorName][capacity]) {
        floorGroups[floorName][capacity] = [];
      }
      floorGroups[floorName][capacity].push(table);
    });
    
    // Sort capacities within each floor
    Object.keys(floorGroups).forEach(floor => {
      const sortedCapacities = Object.keys(floorGroups[floor]).sort((a, b) => parseInt(a) - parseInt(b));
      const sortedGroups = {};
      sortedCapacities.forEach(cap => {
        sortedGroups[cap] = floorGroups[floor][cap];
      });
      floorGroups[floor] = sortedGroups;
    });
    
    // Sort floors
    const sortedFloors = Object.keys(floorGroups).sort((a, b) => {
      const indexA = floorOrder.indexOf(a);
      const indexB = floorOrder.indexOf(b);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
    
    return { floorGroups, sortedFloors };
  };

  const { floorGroups, sortedFloors } = getGroupedTables();

  // Handle table click
  const handleTableClick = (table) => {
    const status = getTableStatus(table);
    if (status === 'available') {
      setSelectedTable(table);
      setBookingForm({
        startTime: new Date().toTimeString().slice(0, 5),
        durationMinutes: 45,
        phoneNumber: '',
        guestName: '',
        bookingDate: selectedDate,
        capacity: table.capacity,
        tableId: table.id,
        paymentEnable: 'false'
      });
      setShowBookingModal(true);
    } else {
      alert(`This table is ${status}. Only available tables can be booked.`);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBookingForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
const handleSubmitBooking = async (e) => {
  e.preventDefault();

  setSubmitting(true);

  try {
    // ✅ Get userId from localStorage
    const userId = localStorage.getItem("userId");

    console.log("🆔 userId from localStorage:", userId);

    if (!userId) {
      throw new Error("User ID not found. Please login again.");
    }

    // ✅ Generate customerId
    const customerId = `USR${Date.now()}`;

    localStorage.setItem("customerId", customerId);

    // ✅ Booking Payload
    const payload = {
      userId: parseInt(userId), // ✅ IMPORTANT

      startTime: bookingForm.startTime + ":00",

      durationMinutes: parseInt(
        bookingForm.durationMinutes || 45
      ),

      phoneNumber: bookingForm.phoneNumber?.trim(),

      guestName: bookingForm.guestName?.trim(),

      bookingDate: bookingForm.bookingDate,

      capacity: parseInt(bookingForm.capacity),

      tableId: parseInt(bookingForm.tableId),

      paymentEnable:
        bookingForm.paymentEnable === true ||
        bookingForm.paymentEnable === "true",

      // ✅ Payment Fields
      paymentMethod: "Online_Payment",

      amount: 9,

      transactionId: `TXN${Date.now()}`,

      orderId: `ORD${Date.now()}`
    };

    console.log("📦 Booking Payload:", payload);

    // ✅ API URL
    const apiUrl = `https://backend.maamaas.com/food/api/seatingdetails/shedule/advance/booking/${userId}?customerId=${customerId}`;

    console.log("🌐 API URL:", apiUrl);

    // ✅ API Call
    const response = await fetch(apiUrl, {
      method: "POST",

      headers: {
        "Content-Type": "application/json",
        ...getHeaders(),
      },

      body: JSON.stringify(payload),
    });

    // ✅ Error Handling
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Unauthorized. Please login again.");
      }

      const errorText = await response.text();

      throw new Error(
        errorText || "Failed to create booking"
      );
    }

    // ✅ Success Response
    const result = await response.json();

    console.log("✅ Booking successful:", result);

    // ✅ Refresh bookings list
    await fetchBookings();

    // ✅ Close Modal
    setShowBookingModal(false);

    // ✅ Success Message
    alert("Booking created successfully!");

       navigate("/Myordermain");

    // ✅ Reset Form
    setBookingForm({
      startTime: "",
      durationMinutes: 45,
      phoneNumber: "",
      guestName: "",
      bookingDate: selectedDate,
      capacity: "",
      tableId: "",
      paymentEnable: "false",
    });

    // ✅ Reset selected table
    setSelectedTable(null);

  } catch (error) {

    console.error(
      "❌ Error creating booking:",
      error
    );

    alert(
      `Failed to create booking: ${error.message}`
    );

  } finally {

    setSubmitting(false);
  }
};
  // Close modal
  const closeModal = () => {
    setShowBookingModal(false);
    setSelectedTable(null);
  };

  // Handle logout or token management (optional)
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    // Redirect to login page or trigger parent component logout
    window.location.href = '/login';
  };

  if (loading) {
    return (
      <div className="tm-container">
        <div className="tm-loading">Loading tables...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tm-container">
        <div className="tm-error">
          <p>Error: {error}</p>
          {error.includes('Unauthorized') && (
            <button onClick={handleLogout} className="tm-logout-btn">
              Go to Login
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="tm-container">
      {/* Date Selector */}
      <div className="tm-date-selector">
        <label>Select Date: </label>
        <input 
          type="date" 
          value={selectedDate} 
          onChange={(e) => setSelectedDate(e.target.value)}
          className="tm-date-input"
        />
        <button onClick={() => { fetchTables(); fetchBookings(); }} className="tm-refresh-btn">
          Refresh
        </button>
      </div>
      
      {/* Statistics Cards */}
      <div className="tm-stats">
        <div className="tm-stat-card">
          <div className="tm-stat-value">{stats.total}</div>
          <div className="tm-stat-label">TOTAL TABLES</div>
        </div>
        <div className="tm-stat-card available-card">
          <div className="tm-stat-value">{stats.available}</div>
          <div className="tm-stat-label">AVAILABLE</div>
        </div>
        <div className="tm-stat-card reserved-card">
          <div className="tm-stat-value">{stats.reserved}</div>
          <div className="tm-stat-label">RESERVED</div>
        </div>
        <div className="tm-stat-card occupied-card">
          <div className="tm-stat-value">{stats.occupied}</div>
          <div className="tm-stat-label">OCCUPIED</div>
        </div>
        <div className="tm-stat-card vacant-card">
          <div className="tm-stat-value">{stats.vacant}</div>
          <div className="tm-stat-label">VACANT</div>
        </div>
        <div className="tm-stat-card cleaning-card">
          <div className="tm-stat-value">{stats.cleaning}</div>
          <div className="tm-stat-label">CLEANING</div>
        </div>
        <div className="tm-stat-card maintenance-card">
          <div className="tm-stat-value">{stats.maintenance}</div>
          <div className="tm-stat-label">MAINTENANCE</div>
        </div>
      </div>

      {/* Status Legend */}
      <div className="tm-legend">
        <span className="tm-legend-title">Status:</span>
        <div className="tm-legend-items">
          <div className="tm-legend-item">
            <div className="tm-dot available-dot"></div>
            <span>Available</span>
          </div>
          <div className="tm-legend-item">
            <div className="tm-dot reserved-dot"></div>
            <span>Reserved</span>
          </div>
          <div className="tm-legend-item">
            <div className="tm-dot occupied-dot"></div>
            <span>Occupied</span>
          </div>
          <div className="tm-legend-item">
            <div className="tm-dot vacant-dot"></div>
            <span>Vacant</span>
          </div>
          <div className="tm-legend-item">
            <div className="tm-dot cleaning-dot"></div>
            <span>Cleaning</span>
          </div>
          <div className="tm-legend-item">
            <div className="tm-dot maintenance-dot"></div>
            <span>Maintenance</span>
          </div>
        </div>
      </div>

      {/* Tables Layout */}
      <div className="tm-tables">
        {sortedFloors.map(floorName => (
          <div key={floorName} className="tm-floor">
            <div className="tm-floor-title">{floorName}</div>
            
            {Object.keys(floorGroups[floorName]).map(capacity => (
              <div key={capacity} className="tm-capacity-section">
                <div className="tm-capacity-title">{capacity} Seater Tables</div>
                <div className="tm-tables-grid">
                  {floorGroups[floorName][capacity].map(table => (
                    <div 
                      key={table.id} 
                      className={`tm-table ${getStatusClass(getTableStatus(table))}`}
                      onClick={() => handleTableClick(table)}
                      style={{ cursor: getTableStatus(table) === 'available' ? 'pointer' : 'not-allowed' }}
                    >
                      <div className="tm-table-code">{table.code}</div>
                      <div className="tm-table-status">{getStatusText(getTableStatus(table))}</div>
                      <div className="tm-table-capacity">Capacity: {table.capacity}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Booking Modal */}
      {showBookingModal && selectedTable && (
        <div className="tm-modal-overlay" onClick={closeModal}>
          <div className="tm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tm-modal-header">
              <h2>Book Table: {selectedTable.code}</h2>
              <button className="tm-modal-close" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmitBooking} className="tm-booking-form">
              <div className="tm-form-group">
                <label>Guest Name *</label>
                <input
                  type="text"
                  name="guestName"
                  value={bookingForm.guestName}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter guest name"
                />
              </div>
              
              <div className="tm-form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={bookingForm.phoneNumber}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter phone number"
                />
              </div>
              
              <div className="tm-form-group">
                <label>Booking Date *</label>
                <input
                  type="date"
                  name="bookingDate"
                  value={bookingForm.bookingDate}
                  onChange={handleInputChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              
              <div className="tm-form-group">
                <label>Start Time *</label>
                <input
                  type="time"
                  name="startTime"
                  value={bookingForm.startTime}
                  onChange={handleInputChange}
                  required
                />
              </div>
              
              <div className="tm-form-group">
                <label>Duration (minutes) *</label>
                <select
                  name="durationMinutes"
                  value={bookingForm.durationMinutes}
                  onChange={handleInputChange}
                  required
                >
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">60 minutes</option>
                  <option value="90">90 minutes</option>
                  <option value="120">120 minutes</option>
                </select>
              </div>
              
              <div className="tm-form-group">
                <label>Capacity *</label>
                <input
                  type="number"
                  name="capacity"
                  value={bookingForm.capacity}
                  onChange={handleInputChange}
                  required
                  readOnly
                  className="tm-readonly"
                />
              </div>
              
              <div className="tm-form-group">
                <label>Table ID</label>
                <input
                  type="text"
                  value={selectedTable.code}
                  readOnly
                  className="tm-readonly"
                />
              </div>
              
              <div className="tm-form-actions">
                <button type="button" onClick={closeModal} className="tm-cancel-btn">
                  Cancel
                </button>
                <button type="submit" disabled={submitting} className="tm-submit-btn">
                  {submitting ? 'Booking...' : 'Confirm Booking'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Usertable;